import { useEffect, useRef } from 'react';

import type { NotificationFrame } from '../stream/supportStreamApi';
import { useSupportStream, useSupportStreamStatus } from '../stream/SupportStreamProvider';

/** How often the count is polled where no live stream reaches (outside /support/*). */
export const UNREAD_POLL_MS = 60_000;

/**
 * Live notification feed for the signed-in agent.
 *
 * Calls `onNotification` once per new notification frame (the stream is
 * at-least-once, so frames are deduped on `notificationId`) and `onResync`
 * whenever the stream asks for on-screen data to be refetched.
 *
 * Returns whether a live stream is reaching this component: outside the
 * Support layout there is none, and callers fall back to polling.
 */
export function useNotificationFeed({
  onNotification,
  onResync,
}: {
  onNotification: (frame: NotificationFrame) => void;
  onResync: () => void;
}): { live: boolean } {
  const seen = useRef(new Set<string>());
  const { status, resyncToken } = useSupportStreamStatus();

  useSupportStream((frame) => {
    if (frame.type !== 'notification') return;
    if (seen.current.has(frame.notificationId)) return;
    seen.current.add(frame.notificationId);
    onNotification(frame);
  });

  const resyncRef = useRef(onResync);
  resyncRef.current = onResync;
  const firstToken = useRef(resyncToken);
  useEffect(() => {
    // The initial token is the first load, which the queries already do.
    if (resyncToken === firstToken.current) return;
    resyncRef.current();
  }, [resyncToken]);

  return { live: status !== 'unavailable' };
}
